import { useAsync } from '@/lib/useAsync';
import { ErrorState, Spinner } from '@/components/ui/States';
import { api } from '@/lib/api';
import { usePortalConfig } from './PortalLayout';

/* The monthly hive report. Totals are live; utilisation, friction and the
 * recommendation are ILLUSTRATIVE until the analytics pipeline ships — every
 * staged block carries the preview badge so nobody mistakes it for data. */
export function PortalReportsPage() {
  const config = usePortalConfig();
  const overview = useAsync(() => api.getPortalOverview(), []);

  if (overview.status === 'loading') return <Spinner label="Preparing the report…" />;
  if (overview.status === 'error') return <ErrorState error={overview.error} onRetry={overview.reload} />;
  const { totals, departments } = overview.data;
  const month = new Date().toLocaleString('en-GB', { month: 'long' });

  return (
    <>
      <div className="portal-head">
        {config.region && (
          <div className="portal-geo">
            {config.region} <span className="car">▾</span>
          </div>
        )}
        <h1>Hive report: {month}</h1>
        <p>How your people and agents work together, what it costs, and where the hive should grow next.</p>
      </div>

      <div className="portal-cols">
        <div>
          <div className="portal-panel">
            <h4>
              The numbers <span className="portal-staged">live</span>
            </h4>
            <div className="portal-row"><span>Agents live</span><span className="meta">{totals.agents}</span></div>
            <div className="portal-row"><span>Departments</span><span className="meta">{totals.departments}</span></div>
            <div className="portal-row"><span>People in the hive</span><span className="meta">{totals.people}</span></div>
            <div className="portal-row"><span>Runs this week</span><span className="meta">{totals.runsThisWeek}</span></div>
            <div className="portal-row"><span>Deliverables this month</span><span className="meta">{totals.deliverablesThisMonth}</span></div>
          </div>
          <div className="portal-panel" style={{ marginTop: 14 }}>
            <h4>
              Agent utilisation <span className="portal-staged">preview</span>
            </h4>
            {departments.slice(0, 6).map((d, i) => {
              const w = [92, 74, 58, 41, 28, 14][i] ?? 10;
              return (
                <div key={d.slug} className="portal-bar-row">
                  <span className="nm">{d.name}</span>
                  <div className="portal-track"><div className="portal-fill" style={{ width: `${w}%` }} /></div>
                  <span className="vv">—</span>
                </div>
              );
            })}
            <p className="portal-hint">
              Live utilisation, adoption and friction tracking land with the reporting layer. The shape above shows
              what this page becomes.
            </p>
          </div>
        </div>
        <div>
          <div className="portal-panel">
            <h4>
              What this report will tell you <span className="portal-staged">preview</span>
            </h4>
            <p className="body" style={{ marginBottom: 8 }}>
              Every month, in this page: which agents carried the load, who on the team uses the hive and who needs a
              hand, where agents were asked to do work they were not built for, and what agent to add next.
            </p>
            <p className="body">
              When the hive spots repeated requests no agent covers, the recommendation appears here with the business
              case, and one click sends it to WarriorLab for scoping.
            </p>
          </div>
          <div className="portal-panel" style={{ marginTop: 14 }}>
            <h4>
              Billing <span className="portal-staged">preview</span>
            </h4>
            <p className="body">
              Licensing per live agent plus hosting, itemised here monthly. Paused agents don't bill; archived agents
              keep their knowledge at no charge.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
